/**
 * IELTS Listening (D-105): 4 parts, one after the other; the recording is played ONCE (the audio
 * of the part, or the en-GB voice reading its script). Answers are saved as they are given.
 * IELTS on computer (D-163, `state.navigable`): the recording runs on from one part to the next
 * by itself, the 40 questions stay open (tabs by part) and every answer can be changed until the
 * section ends, with 2 minutes of review after the last recording. Without it (an older table),
 * « Next part » closes the part for good. Training mode: a full player instead (pause, back,
 * listen again at will, D-144).
 */

import { academyAttemptsNextCreate, academyAttemptsPartCreate, academyAttemptsPassageAudioRetrieve } from "@agencyos/api-client";
import { type IeltsState, instructionFor, planLines, splitScript } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { ArrowRight, Headphones, Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../../lib/storage";
import { OnceAudio, speechAvailable, stopSpeaking, unlockOnFirstGesture } from "../audio";
import { speakLines, stopLines } from "../listening";
import { ExamAbandon, ExamAudioPanel, type ExamCell, ExamNav, revealItem, sequenceCells } from "../ExamFrame";
import { PlanPicture } from "../ExamPicture";
import { TrainingListen, TrainingNote } from "../ListeningReplay";
import { SectionDone } from "../McqSection";
import { QuestionBlock, numberLabel, useAnswers } from "./IeltsQuestions";

type Listen = "idle" | "loading" | "playing" | "done";
const WAVE = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const heardKey = (attemptId: string, passageId: string) => `academy.ielts.heard.${attemptId}.${passageId}`;

/** A plan / map to label (D-136): the plan described in words, each lettered place on its line. */
function PlanCard({ description }: { description: string }) {
  const { t } = useTranslation();
  const plan = planLines(description);
  return (
    <figure className="ac-plan" lang="en">
      <figcaption className="ac-plan__title">{t("ielts.player.plan")}</figcaption>
      {plan.intro && <p className="ac-secondary">{plan.intro}</p>}
      {plan.positions.length > 0 && (
        <dl className="ac-plan__list">
          {plan.positions.map((p) => (
            <div key={p.letter} className="ac-plan__row">
              <dt className="ac-plan__letter ao-num">{p.letter}</dt>
              <dd>{p.text}</dd>
            </div>
          ))}
        </dl>
      )}
    </figure>
  );
}

export function ListeningSection({
  attemptId,
  state,
  training = false,
  onChanged,
}: {
  attemptId: string;
  state: IeltsState;
  training?: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const group = state.current;
  const passage = group?.passage ?? null;
  const questions = group?.questions ?? [];
  const passageKey = passage?.id ?? `part-${state.part}`;
  const [listen, setListen] = useState<Listen>(() => (readStorage(heardKey(attemptId, passageKey)) === "1" ? "done" : "idle"));
  const [audioProblem, setAudioProblem] = useState(false);
  const [linkFailed, setLinkFailed] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [moving, setMoving] = useState(false);
  const audio = useRef<OnceAudio | null>(null);
  const { set, flush, valueOf, isAnswered, saves } = useAnswers(attemptId, onChanged);
  // D-163: IELTS on computer — every part stays open, the view follows the recording
  const free = state.navigable;
  const [view, setView] = useState(() => Math.min(Math.max(state.part - 1, 0), Math.max(state.sections.length - 1, 0)));
  const [focused, setFocused] = useState<number | null>(null); // the number the learner is on
  const advance = useRef<() => void>(() => undefined);
  const passageId = passage?.id ?? "";
  const loadAudio = useCallback(
    async () => (await academyAttemptsPassageAudioRetrieve(attemptId, passageId)).data.url,
    [attemptId, passageId],
  );
  const script = passage?.scriptForSpeech ?? "";
  const trainingLines = useMemo(() => (script ? splitScript(script) : []), [script]);

  useEffect(() => {
    unlockOnFirstGesture();
    return () => {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
    };
  }, []);

  const heard = () => {
    writeStorage(heardKey(attemptId, passageKey), "1");
    setListen("done");
    // the recording runs on: the next part starts by itself (never after the last one)
    if (free && !training && state.part < state.parts) advance.current();
  };

  const play = async () => {
    if (audio.current) {
      // the start was blocked (Safari: the link came after the click): the same listening
      setListen("playing");
      if ((await audio.current.resume()) === "blocked") setListen("idle");
      return;
    }
    // Marked as heard as soon as it starts: a reload does not give a second listening.
    writeStorage(heardKey(attemptId, passageKey), "1");
    if (passage?.hasAudio) {
      setListen("loading");
      setLinkFailed(false);
      try {
        const response = await academyAttemptsPassageAudioRetrieve(attemptId, passage.id);
        const player = new OnceAudio({
          onEnded: heard,
          onFailed: () => {
            setAudioProblem(true);
            heard();
          },
        });
        audio.current = player;
        setListen("playing");
        if ((await player.start(response.data.url)) === "blocked") setListen("idle");
      } catch {
        // D-182: no link, nothing was heard: the same listening starts again, never the next part
        writeStorage(heardKey(attemptId, passage.id), null);
        setLinkFailed(true);
        setListen("idle");
      }
      return;
    }
    if (!speechAvailable() || !passage?.scriptForSpeech) {
      setAudioProblem(true);
      heard();
      return;
    }
    setListen("playing");
    // one voice per speaker, the names never read, in short pieces (D-106, D-114)
    const outcome = await speakLines(splitScript(passage.scriptForSpeech), { lang: "en", preferred: "en-GB" });
    if (outcome === "blocked") setListen("idle");
    else heard();
  };

  const allQuestions = free ? state.sections.flatMap((s) => s.questions) : questions;
  const nextPart = async () => {
    setMoving(true);
    try {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
      await flush(allQuestions);
      await academyAttemptsPartCreate(attemptId);
    } catch {
      // Already closed (time up): the refreshed state shows what comes next.
    } finally {
      await onChanged();
      setMoving(false);
      setConfirm(false);
    }
  };

  advance.current = () => void nextPart();
  const finishFree = async () => {
    setMoving(true);
    try {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
      await flush(allQuestions);
      await academyAttemptsNextCreate(attemptId);
    } catch {
      // Already closed (time up): the refreshed state shows what comes next.
    } finally {
      await onChanged();
      setMoving(false);
      setConfirm(false);
    }
  };

  // D-163: the recording runs on — the next part starts by itself when the previous one was
  // heard (a browser that blocks it shows the play button instead)
  const playLatest = useRef(play);
  playLatest.current = play;
  const autoStarted = useRef(false);
  const previousId = free && state.part > 1 ? (state.sections[state.part - 2]?.passage?.id ?? "") : "";
  useEffect(() => {
    if (!previousId || training || autoStarted.current || listen !== "idle") return;
    if (readStorage(heardKey(attemptId, previousId)) !== "1") return;
    autoStarted.current = true;
    void playLatest.current();
  }, [attemptId, listen, previousId, training]);

  if (!group) return <SectionDone onContinue={() => void nextPart()} busy={moving} />;

  const last = state.part >= state.parts;
  // D-163: the questions shown — the part chosen in the tabs (IELTS on computer), else the
  // part being heard
  const shown = free ? (state.sections[view] ?? group) : group;
  const shownPassage = shown.passage;
  const shownQuestions = shown.questions;
  const shownPart = free ? view + 1 : state.part;
  const answered = shownQuestions.filter(isAnswered).length;
  const answeredAll = allQuestions.filter(isAnswered).length;
  const reviewing = free && last && listen === "done" && !training;
  let counter = 0;

  // the grid: every number in free navigation (IELTS on computer, D-163); else the current part
  // only is open, the others are shown without a way to them
  let running = 0;
  const cells: ExamCell[] = free
    ? state.sections.flatMap((item, s) =>
        item.questions.map((question) => {
          const { text, first } = numberLabel(question, running + 1);
          running = first + Math.max(question.marks, 1) - 1;
          return {
            key: question.id,
            text,
            state: isAnswered(question) ? ("done" as const) : ("empty" as const),
            current: s === view && first === (focused ?? numberLabel(item.questions[0] ?? question, first).first),
            onSelect: () => {
              setView(s);
              setFocused(first);
              revealItem(`iq-anchor-${first}`);
            },
          };
        }),
      )
    : sequenceCells(
        state.total,
        questions.map((q, index) => ({ number: q.number ?? (state.part - 1) * 10 + index + 1, answered: isAnswered(q) })),
        state.answered + 1,
      );

  return (
    <div className="ac-listening">
      <ExamNav cells={cells} />
      <section className="ac-question__sheet ac-listening__head" aria-labelledby="ielts-part-title">
        <div className="ac-listening__intro">
          <p className="ac-player__eyebrow ao-num">
            {free ? t("conformity.ielts.recordingOf", { n: state.part, total: state.parts || 4 }) : t("ielts.player.partOf", { n: state.part, total: state.parts || 4 })}
          </p>
          <h2 id="ielts-part-title" className="ac-title-3" lang="en">
            {passage?.title || passage?.context || t("ielts.player.partN", { n: state.part })}
          </h2>
          {passage?.context && passage.title && passage.context !== passage.title && (
            <p className="ac-secondary" lang="en">
              {passage.context}
            </p>
          )}
          {!free && passage?.instructions && (
            <p className="ac-item__instructions" lang="en">
              {passage.instructions}
            </p>
          )}
          {!free && (
            <PlanPicture picture={passage?.image ?? null} lang="en">
              {passage?.mapDescription ? <PlanCard description={passage.mapDescription} /> : null}
            </PlanPicture>
          )}
        </div>
        <ExamAudioPanel
          playing={!training && listen === "playing" && Boolean(passage?.hasAudio)}
          note={
            training ? (
              <TrainingNote />
            ) : (
              <>
                <p className="ac-footnote">{t("ielts.listen.once")}</p>
                <p className="ac-footnote">{t("listening.training.tip")}</p>
              </>
            )
          }
        >
        {training ? (
          <div lang="en">
            <TrainingListen
              load={passage?.hasAudio ? loadAudio : null}
              lines={trainingLines}
              lang="en"
              preferred="en-GB"
              onStart={() => {
                if (listen !== "done") heard();
              }}
            />
          </div>
        ) : (
          <div className={`ac-listen ac-listen--${listen}`}>
            {listen === "idle" && (
              <button type="button" className="ac-listen__button" onClick={() => void play()}>
                <span className="ac-listen__disc" aria-hidden="true">
                  <Play size={30} strokeWidth={2.2} />
                </span>
                <span className="ac-listen__label">{t("ielts.listen.playPart", { n: state.part })}</span>
              </button>
            )}
            {(listen === "loading" || listen === "playing") && (
              <div className="ac-listen__playing" role="status">
                <span className="ac-wave" aria-hidden="true">
                  {WAVE.map((n) => (
                    <span key={n} style={{ animationDelay: `${n * -0.13}s` }} />
                  ))}
                </span>
                <span>{t("ielts.listen.playingPart")}</span>
              </div>
            )}
            {listen === "done" && (
              <p className="ac-status ac-status--success" role="status">
                <Headphones aria-hidden size={16} />
                {reviewing ? t("conformity.ielts.reviewTime", { count: state.reviewMinutes || 2 }) : t("ielts.listen.partHeard")}
              </p>
            )}
            {audioProblem && <p className="ko ac-footnote">{t("academy.player.audioProblem")}</p>}
            {linkFailed && (
              <p className="ko ac-footnote" role="alert">
                {t("academy.player.audioRetry")}
              </p>
            )}
          </div>
        )}
        </ExamAudioPanel>
      </section>

      <section className="ac-question__sheet" aria-label={t("ielts.player.questions")}>
        {free && (
          <div className="ac-task-tabs" role="tablist" aria-label={t("conformity.ielts.parts")}>
            {state.sections.map((item, index) => {
              const done = item.questions.filter(isAnswered).length;
              return (
                <button
                  key={item.passage?.id ?? index}
                  type="button"
                  role="tab"
                  aria-selected={index === view}
                  className={`ac-task-tab${done === item.questions.length ? " ac-task-tab--sent" : ""}`}
                  onClick={() => {
                    setView(index);
                    setFocused(null);
                  }}
                >
                  <span className="ac-task-tab__n ao-num" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span>{t("ielts.player.partN", { n: index + 1 })}</span>
                  <span className="ac-task-tab__words ao-num" aria-hidden="true">
                    {done}/{item.questions.length}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {free && shownPassage?.instructions && (
          <p className="ac-item__instructions" lang="en">
            {shownPassage.instructions}
          </p>
        )}
        {free && (
          <PlanPicture picture={shownPassage?.image ?? null} lang="en">
            {shownPassage?.mapDescription ? <PlanCard description={shownPassage.mapDescription} /> : null}
          </PlanPicture>
        )}
        <div className="ac-question__progress">
          <p className="ac-question__count ao-num">{t("ielts.player.answeredOf", { n: answered, total: shownQuestions.length })}</p>
        </div>
        <ol
          className="ac-iq-list"
          onFocus={(event) => {
            const anchor = (event.target as HTMLElement).closest?.("[id^='iq-anchor-']");
            if (anchor) setFocused(Number(anchor.id.replace("iq-anchor-", "")));
          }}
        >
          {shownQuestions.map((question) => {
            counter += 1;
            return (
              <QuestionBlock
                key={question.id}
                rubric={instructionFor(shownPassage?.instructionGroups ?? [], question.number, question.numbers, counter === 1)}
                skill="co"
                question={question}
                fallbackNumber={(shownPart - 1) * 10 + counter}
                value={valueOf(question)}
                onChange={(value) => set(question, value)}
                saveState={saves[question.id]}
              />
            );
          })}
        </ol>
      </section>

      {free ? (
        <div className="ac-finish ac-listening__finish">
          <p className="ac-caption ao-num" role="status">
            {t("academy.navigator.answered", { answered: answeredAll, total: allQuestions.length })}
          </p>
          {confirm ? (
            <div className="ac-finish__confirm" role="alertdialog" aria-label={t("ielts.player.finishListening")}>
              <p>{t("conformity.ielts.finishConfirm", { count: allQuestions.length - answeredAll })}</p>
              <div className="ac-card__actions">
                <Button variant="ghost" onClick={() => setConfirm(false)}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={() => void finishFree()} disabled={moving}>
                  {moving ? t("common.loading") : t("ielts.player.finishListening")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="ac-card__actions">
              {/* the next recording, when it did not start by itself (a blocked autoplay) or in training */}
              {!last && (listen === "done" || training) && (
                <Button variant="secondary" onClick={() => void nextPart()} disabled={moving}>
                  {t("conformity.ielts.nextRecording")}
                  <ArrowRight aria-hidden size={18} />
                </Button>
              )}
              <Button size="lg" variant={reviewing ? "primary" : "secondary"} onClick={() => setConfirm(true)}>
                {t("ielts.player.finishListening")}
              </Button>
            </div>
          )}
          <p className="ac-footnote">{t("conformity.ielts.changeUntilEnd")}</p>
        </div>
      ) : (
        <div className="ac-finish ac-listening__finish">
          {confirm ? (
            <div className="ac-finish__confirm" role="alertdialog" aria-label={t("ielts.player.nextPart")}>
              <p>{last ? t("ielts.player.lastPartConfirm") : t("ielts.player.nextPartConfirm")}</p>
              <div className="ac-card__actions">
                <Button variant="ghost" onClick={() => setConfirm(false)}>
                  {t("common.cancel")}
                </Button>
                <Button onClick={() => void nextPart()} disabled={moving}>
                  {moving ? t("common.loading") : last ? t("ielts.player.finishListening") : t("ielts.player.nextPart")}
                </Button>
              </div>
            </div>
          ) : (
            <Button size="lg" variant={listen === "done" ? "primary" : "secondary"} onClick={() => setConfirm(true)}>
              {last ? t("ielts.player.finishListening") : t("ielts.player.nextPart")}
              <ArrowRight aria-hidden size={18} />
            </Button>
          )}
          {!training && !confirm && <ExamAbandon />}
          <p className="ac-footnote">{t("ielts.player.noBackPart")}</p>
        </div>
      )}
    </div>
  );
}
